import { addWeeks, endOfDay, formatISO, max, startOfDay, startOfToday, subDays } from 'date-fns';
import { startTransition, useCallback, useOptimistic } from 'react';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

export const beginFilterKeys = {
  start: 'start',
  end: 'end',
  option: 'option',
} as const;

export const filterOptions = {
  TODAY: 'today',
  WITHIN_4_WEEKS: 'within4weeks',
  AFTER_4_WEEKS: 'after4weeks',
  CUSTOM: 'custom',
} as const;

export const filterOptionSchema = z.enum(filterOptions);
export type FilterOption = (typeof filterOptions)[keyof typeof filterOptions];

export const predefinedOptions: { value: FilterOption; label: string }[] = [
  { value: filterOptions.TODAY, label: 'Ab heute' },
  { value: filterOptions.WITHIN_4_WEEKS, label: 'In weniger als 4 Wochen' },
  { value: filterOptions.AFTER_4_WEEKS, label: 'In mehr als 4 Wochen' },
  { value: filterOptions.CUSTOM, label: 'Benutzerdefiniert' },
] as const;

export function dateToString(date: Date) {
  return formatISO(date, { representation: 'date' });
}

export function stringToDate(value: string | null) {
  if (!value) {
    return null;
  }

  const result = new Date(value);
  if (Number.isNaN(result.getDate())) {
    return null;
  }
  return result;
}

export function sanitizeStartDate(date: Date) {
  return startOfDay(max([date, subDays(startOfToday(), 7)]));
}

export function sanitizeEndDate(startDate: Date | null, endDate: Date) {
  const start = startDate ?? startOfToday();
  if (endDate < start) {
    return null;
  }
  return endOfDay(endDate);
}

function setOptionInSearchParams(option: FilterOption, params: URLSearchParams) {
  params.set(beginFilterKeys.option, option);
  switch (option) {
    case filterOptions.CUSTOM:
      break;

    case filterOptions.TODAY:
      params.set(beginFilterKeys.start, dateToString(startOfToday()));
      params.delete(beginFilterKeys.end);
      break;

    case filterOptions.WITHIN_4_WEEKS: {
      const today = startOfToday();
      params.set(beginFilterKeys.start, dateToString(today));
      params.set(beginFilterKeys.end, dateToString(endOfDay(addWeeks(today, 4))));
      break;
    }

    case filterOptions.AFTER_4_WEEKS: {
      params.set(beginFilterKeys.start, dateToString(addWeeks(startOfToday(), 4)));
      params.delete(beginFilterKeys.end);
    }
  }
}

export function setDatesInSearchParams(startDate: Date | null, endDate: Date | null, params: URLSearchParams) {
  if (startDate) {
    startDate = sanitizeStartDate(startDate);
    if (endDate) {
      endDate = sanitizeEndDate(startDate, endDate);
    }
  }

  if (startDate) {
    params.set(beginFilterKeys.start, dateToString(startDate));
  } else {
    params.delete(beginFilterKeys.start);
  }

  if (endDate) {
    params.set(beginFilterKeys.end, dateToString(endDate));
  } else {
    params.delete(beginFilterKeys.end);
  }
}

export function initializeBeginFilterParams(params: URLSearchParams) {
  const storedOption = filterOptionSchema.nullable().parse(params.get(beginFilterKeys.option));
  if (!storedOption) {
    setOptionInSearchParams(filterOptions.WITHIN_4_WEEKS, params);
  } else if (storedOption === filterOptions.CUSTOM) {
    // Otherwise, sanitize both start and end date
    const storedStartDate = stringToDate(params.get(beginFilterKeys.start));
    const storedEndDate = stringToDate(params.get(beginFilterKeys.end));
    setDatesInSearchParams(storedStartDate, storedEndDate, params);
  } else {
    // If an option is set, we ignore the dates
    setOptionInSearchParams(storedOption, params);
  }
}

export function useBeginFilterParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const actualOption = filterOptionSchema.nullable().parse(searchParams.get(beginFilterKeys.option));
  const actualStartDate = stringToDate(searchParams.get(beginFilterKeys.start));
  const actualEndDate = stringToDate(searchParams.get(beginFilterKeys.end));

  const [optimisticOption, setOptimisticOption] = useOptimistic(
    actualOption,
    (_, newValue: FilterOption | null) => newValue,
  );
  const [optimisticStartDate, setOptimisticStartDate] = useOptimistic(
    actualStartDate,
    (_, newValue: Date | null) => newValue,
  );
  const [optimisticEndDate, setOptimisticEndDate] = useOptimistic(
    actualEndDate,
    (_, newValue: Date | null) => newValue,
  );

  const setOption = useCallback(
    (option: FilterOption) => {
      startTransition(() => {
        setOptimisticOption(option);
        setSearchParams(
          // The prev parameter from react-router may be stale, see https://github.com/remix-run/react-router/issues/9757
          () => {
            const prev = new URLSearchParams(window.location.search);
            setOptionInSearchParams(option, prev);
            return prev;
          },
          { replace: true },
        );
      });
    },
    [setSearchParams, setOptimisticOption],
  );

  const setDates = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      startTransition(() => {
        setOptimisticStartDate(startDate);
        setOptimisticEndDate(endDate);
        setSearchParams(() => {
          // The prev parameter from react-router may be stale, see https://github.com/remix-run/react-router/issues/9757
          const prev = new URLSearchParams(window.location.search);
          setDatesInSearchParams(startDate, endDate, prev);
          setOptionInSearchParams(filterOptions.CUSTOM, prev);
          return prev;
        });
      });
    },
    [setSearchParams, setOptimisticStartDate, setOptimisticEndDate],
  );

  return [
    { option: optimisticOption, startDate: optimisticStartDate, endDate: optimisticEndDate },
    { setOption, setDates },
  ] as const;
}
